import { useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { useI18n } from '@/context/LanguageContext';

const DATASETS = {
  lithium: {
    line: [
      { m: 'Ene', v: 12800 },
      { m: 'Mar', v: 13200 },
      { m: 'May', v: 12600 },
      { m: 'Jul', v: 13900 },
      { m: 'Sep', v: 14100 },
      { m: 'Nov', v: 14850 },
    ],
    bar: [
      { q: 'T1', v: 17 },
      { q: 'T2', v: 19 },
      { q: 'T3', v: 20 },
      { q: 'T4', v: 21 },
    ],
  },
  oilgas: {
    line: [
      { m: 'Ene', v: 64 },
      { m: 'Mar', v: 66 },
      { m: 'May', v: 63 },
      { m: 'Jul', v: 67 },
      { m: 'Sep', v: 69 },
      { m: 'Nov', v: 68 },
    ],
    bar: [
      { q: 'T1', v: 390 },
      { q: 'T2', v: 410 },
      { q: 'T3', v: 432 },
      { q: 'T4', v: 447 },
    ],
  },
  macro: {
    line: [
      { m: 'Ene', v: 100 },
      { m: 'Mar', v: 112 },
      { m: 'May', v: 121 },
      { m: 'Jul', v: 118 },
      { m: 'Sep', v: 128 },
      { m: 'Nov', v: 131 },
    ],
    bar: [
      { q: 'T1', v: 7.9 },
      { q: 'T2', v: 8.6 },
      { q: 'T3', v: 9.2 },
      { q: 'T4', v: 9.87 },
    ],
  },
};

const BLUE = 'hsl(214, 70%, 30%)';
const TERRA = 'hsl(18, 62%, 46%)';
const GRID = 'hsl(214, 18%, 86%)';

function Kpi({ label, value, delta }) {
  const negative = String(delta).trim().startsWith('-');
  return (
    <Card className="border-border/70 p-4 shadow-[var(--shadow-xs)]">
      <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 font-mono text-xl font-medium tracking-tight text-[hsl(var(--inv-slate))]">
        {value}
      </p>
      <p
        className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${
          negative ? 'text-destructive' : 'text-[hsl(188,45%,30%)]'
        }`}
      >
        {negative ? <ArrowDownRight className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
        {delta}
      </p>
    </Card>
  );
}

function Panel({ tabKey }) {
  const { t } = useI18n();
  const kpis = t(`market.kpis.${tabKey}`);
  const data = DATASETS[tabKey];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {Array.isArray(kpis) &&
          kpis.map((k, i) => <Kpi key={i} label={k.label} value={k.value} delta={k.delta} />)}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-border/70 p-4">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {t('market.series.priceTrend')}
          </p>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.line} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: 'hsl(215 12% 42%)' }} tickLine={false} axisLine={{ stroke: GRID }} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(215 12% 42%)' }} tickLine={false} axisLine={false} width={44} />
                <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${GRID}`, fontSize: 12 }} />
                <Line type="monotone" dataKey="v" stroke={BLUE} strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="border-border/70 p-4">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {t('market.series.production')}
          </p>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.bar} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke={GRID} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="q" tick={{ fontSize: 11, fill: 'hsl(215 12% 42%)' }} tickLine={false} axisLine={{ stroke: GRID }} />
                <YAxis tick={{ fontSize: 11, fill: 'hsl(215 12% 42%)' }} tickLine={false} axisLine={false} width={44} />
                <Tooltip cursor={{ fill: 'hsl(210 18% 95%)' }} contentStyle={{ borderRadius: 10, border: `1px solid ${GRID}`, fontSize: 12 }} />
                <Bar dataKey="v" fill={TERRA} radius={[4, 4, 0, 0]} maxBarSize={38} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      <p className="font-mono text-[11px] text-muted-foreground">{t('market.note')}</p>
    </div>
  );
}

export function MarketDashboard() {
  const { t } = useI18n();
  const [tab, setTab] = useState('lithium');
  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full">
      <TabsList className="mb-6 bg-[hsl(var(--muted))]">
        <TabsTrigger value="lithium" data-testid="market-tab-lithium">{t('market.tabs.lithium')}</TabsTrigger>
        <TabsTrigger value="oilgas" data-testid="market-tab-oilgas">{t('market.tabs.oilgas')}</TabsTrigger>
        <TabsTrigger value="macro" data-testid="market-tab-macro">{t('market.tabs.macro')}</TabsTrigger>
      </TabsList>
      <TabsContent value="lithium"><Panel tabKey="lithium" /></TabsContent>
      <TabsContent value="oilgas"><Panel tabKey="oilgas" /></TabsContent>
      <TabsContent value="macro"><Panel tabKey="macro" /></TabsContent>
    </Tabs>
  );
}

export default MarketDashboard;
